import React from 'react';

import { usePopup, useAuthorizeSigningRequest } from '@/providers';
import { FullscreenModal } from '@infinex-private/connect-web-shared/components';
import type { ChainKey } from '@infinex/chain-config';
import {
  CircleHelpFillIcon,
  StatusVerifiedFilledIcon,
} from '@infinex/ui-icons';
import { Text } from '@infinex/ui/components';

import { DetailsSection } from './DetailsSection';
import { EstimatedWalletChangesRow } from './EstimatedWalletChangesRow';
import { RawDataSection } from './RawDataSection';

import type { ChainValidationResponse } from '../../types';
import { MaliciousTransactionCard } from '../MaliciousTransactionCard';
import { SignableInfoItem } from '../SignableInfoItem';
import type { NormalizedValidationResult } from '../ValidationResultCard/normalizeChainResponse';

interface TransactionRequestInfoProps {
  hostname: string;
  verified: boolean;
  onBack: () => void;
  isTransaction: boolean;
  validationResult: NormalizedValidationResult | null;
  maliciousDescription?: string;
  chainKey?: ChainKey;
}

export function TransactionRequestInfo({
  hostname,
  verified,
  onBack,
  isTransaction,
  validationResult,
  maliciousDescription,
  chainKey,
}: TransactionRequestInfoProps) {
  const { popupData } = usePopup() as { popupData: ChainValidationResponse };
  const { metadata } = useAuthorizeSigningRequest();
  console.log(
    'popupData',
    popupData,
    ' chainKey',
    chainKey,
    ' validationResult',
    validationResult,
    ' maliciousDescription',
    maliciousDescription
  );
  const diffItems = validationResult?.assetDiffs || [];
  const approvalItems = validationResult?.assetApprovals || [];

  // Separate sending and receiving items
  const sendingItems = diffItems.filter((item) => !item.isReceiving);
  const receivingItems = diffItems.filter((item) => item.isReceiving);

  return (
    <FullscreenModal
      onBack={onBack}
      className="bg-canvas h-full w-full rounded-none"
    >
      {/* Content */}
      <div className="mt-0 flex-1 p-4">
        <Text className="text-emphasis text-2xl font-semibold">
          {isTransaction ? 'Transaction request' : 'Signing request'}
        </Text>

        {/* Verified site info or malicious flag*/}
        {maliciousDescription ? (
          <MaliciousTransactionCard className="my-2" />
        ) : (
          <div className="bg-surface mt-5 flex items-center gap-3 rounded-2xl px-5 py-4">
            {verified ? (
              <div className="flex items-center gap-2">
                <StatusVerifiedFilledIcon
                  aria-label="Verified site"
                  fill="info"
                />
                <Text className="text-secondary text-sm font-medium">
                  {hostname} is a verified site
                </Text>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CircleHelpFillIcon fill="secondary" className="size-5" />
                <div className="flex-1">
                  <Text className="text-secondary text-sm font-medium">
                    This domain hasn't been verified
                  </Text>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Estimated wallet changes */}
        {(sendingItems.length > 0 ||
          receivingItems.length > 0 ||
          approvalItems.length > 0) && (
          <div className="bg-surface mt-3 space-y-4 rounded-3xl px-5 py-6">
            <Text className="text-tertiary text-sm font-semibold">
              Estimated wallet changes
            </Text>

            <div className="space-y-3">
              {/* Sending section */}
              {sendingItems.length > 0 && (
                <div className="space-y-3">
                  {sendingItems.map((item, index) => (
                    <EstimatedWalletChangesRow
                      key={`sending-${item.asset.type === 'native' ? 'native' : item.asset.address}-${index}`}
                      item={item}
                      type="diff"
                    />
                  ))}
                </div>
              )}

              {/* Receiving section */}
              {receivingItems.length > 0 && (
                <div className="space-y-3">
                  {receivingItems.map((item, index) => (
                    <EstimatedWalletChangesRow
                      key={`receiving-${item.asset.type === 'native' ? 'native' : item.asset.address}-${index}`}
                      item={item}
                      type="diff"
                    />
                  ))}
                </div>
              )}

              {/* Approvals section */}
              {approvalItems.length > 0 && (
                <div className="space-y-3">
                  {approvalItems.map((item, index) => (
                    <EstimatedWalletChangesRow
                      key={`approval-${item.asset.type === 'native' ? 'native' : item.asset.address}-${item.spenderAddress}-${index}`}
                      item={item}
                      type="approval"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transaction Details */}
        {isTransaction && metadata?.blockaidValidationResult && chainKey && (
          <DetailsSection
            txnValidationResult={metadata.blockaidValidationResult}
            chainKey={chainKey}
          />
        )}

        {/* Raw Data Section */}
        {isTransaction ? (
          <RawDataSection rawRequest={popupData.rawRequest} />
        ) : (
          <div className={`bg-surface mt-4 rounded-3xl p-4`}>
            <SignableInfoItem
              rawData={popupData.rawRequest}
              expandable={false}
              title="Raw details"
            />
          </div>
        )}
      </div>
    </FullscreenModal>
  );
}
